/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { deadlinesQuery } from '@/api/deadlines';
import {
  noticesQuery,
  useMarkAllNoticesRead,
  useMarkNoticeRead,
  type Notice,
} from '@/api/notifications';
import { Icon } from '@/components/ui/Icon';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { toast } from '@/components/ui/Toast';
import { formatDate } from '@/lib/dates';
import {
  noticeDestination,
  noticeHeadline,
  noticeSubject,
  noticeWhy,
  noticeWhen,
} from '@/lib/notices';

/** Wide enough for a headline and a title on one line most of the time. */
const PANEL_WIDTH = 320;

/** Past this the badge says "9+": the number is a prompt, not a count to read. */
const BADGE_MAX = 9;

/**
 * The bell: what happened that concerns you, and what is coming up.
 *
 * The number on it is the number of **unread notices** and nothing else
 * (`NOT-16`). Deadlines are listed under their own heading but do not add to
 * it: a number that also counted dates would never reach zero while an
 * agreement sat overdue, and a count that cannot be cleared stops being read.
 *
 * The notices are checked once a minute while the tab is in front
 * (`NOT-18`). That check is not activity: the session guard only counts what
 * a person does -- a key, a click, a scroll, a touch -- so a timer refetching
 * in the background never holds off the idle sign-out (`SESS-5`).
 */
export function Bell() {
  const { data } = useQuery(noticesQuery());
  const unread = data?.unread_count ?? 0;

  return (
    <Menu
      label="Notifications"
      width={PANEL_WIDTH}
      trigger={(props) => (
        <button
          type="button"
          className="ib"
          {...props}
          aria-label={
            unread
              ? `Notifications, ${String(unread)} unread`
              : 'Notifications, nothing unread'
          }
        >
          <Icon name="bell" size={16} />
          {unread > 0 ? (
            <span className="bell-count" aria-hidden>
              {unread > BADGE_MAX ? `${String(BADGE_MAX)}+` : unread}
            </span>
          ) : null}
        </button>
      )}
    >
      {(close) => <Panel onGo={close} />}
    </Menu>
  );
}

function Panel({ onGo }: { onGo: () => void }) {
  const navigate = useNavigate();
  const notices = useQuery(noticesQuery());
  const markRead = useMarkNoticeRead();
  const markAll = useMarkAllNoticesRead();
  // Read once, when the panel opens: "5 minutes ago" is accurate enough for
  // as long as anybody holds a menu open.
  const [now] = useState(() => Date.now());

  const open = (notice: Notice) => {
    if (notice.read_at === null) markRead.mutate(notice.id);
    onGo();
    // Through the agreement's own page, and its own permission checks
    // (`NOT-17`). The server says whether the reader still can; the page
    // decides again when it loads. An out-of-office notice about the period
    // itself has no agreement, and opens the Out of office screen instead.
    const where = noticeDestination(notice);
    if (where?.to === '/agreements/$agreementId') {
      void navigate({ to: where.to, params: where.params });
    } else if (where?.to === '/agreements') {
      void navigate({ to: where.to, search: where.search });
    } else if (where?.to === '/settings/out-of-office') {
      void navigate({ to: where.to });
    } else if (where?.to === '/settings/people') {
      void navigate({ to: where.to });
    } else {
      toast('You can no longer open this agreement');
    }
  };

  const unread = notices.data?.unread_count ?? 0;
  const items = notices.data?.items ?? [];

  return (
    <>
      <div className="bell-head">
        <span>Notifications</span>
        {unread > 0 ? (
          <button
            type="button"
            className="bell-all"
            onClick={() => {
              markAll.mutate(undefined, {
                onError: () => {
                  toast('Those could not be marked as read. Try again in a moment.');
                },
              });
            }}
          >
            Mark all as read
          </button>
        ) : null}
      </div>

      {notices.isPending ? (
        <div className="sk bell-sk" aria-label="Loading notifications">
          {[0, 1, 2].map((row) => (
            <div className="sk-row" key={row}>
              <span className="sk-bar" />
            </div>
          ))}
        </div>
      ) : notices.isError ? (
        <div className="bell-note">
          <span>Your notifications could not be loaded.</span>
          <button
            type="button"
            className="bell-all"
            onClick={() => {
              void notices.refetch();
            }}
          >
            Try again
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="bell-note">
          Nothing new. When something happens to an agreement you are part of, it shows
          here.
        </p>
      ) : (
        <ul className="bell-list" role="none">
          {items.map((notice) => (
            <li key={notice.id} role="none">
              <button
                type="button"
                role="menuitem"
                className={notice.read_at === null ? 'notice unread' : 'notice'}
                onClick={() => {
                  open(notice);
                }}
              >
                <span className="notice-dot" aria-hidden />
                <span>
                  <b>{noticeHeadline(notice, new Date(now))}</b>
                  {/* About a period, not an agreement: no reference, no
                      counterparty, and no empty line where they would be. */}
                  {noticeSubject(notice) ? <small>{noticeSubject(notice)}</small> : null}
                  {noticeWhy(notice) ? <small>{noticeWhy(notice)}</small> : null}
                  <small className="notice-when">
                    {noticeWhen(notice.created_at, now)}
                    {notice.read_at === null ? (
                      <span className="sr">, unread</span>
                    ) : null}
                  </small>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <hr />
      <ComingUp onGo={onGo} />
      <hr />
      <MenuItem
        icon={<Icon name="gear" size={15} />}
        title="Notification settings"
        onClick={() => {
          onGo();
          void navigate({ to: '/settings/notifications' });
        }}
      />
    </>
  );
}

/**
 * What is coming up, drawn from the same deadlines the Deadlines screen shows.
 *
 * Not copied into the notices: two sources for one date is how they come to
 * disagree.
 */
function ComingUp({ onGo }: { onGo: () => void }) {
  const navigate = useNavigate();
  const { data } = useQuery(deadlinesQuery());
  const soon = (data ?? []).filter((d) => d.urgency !== 'later').slice(0, 4);

  return (
    <>
      <div className="bell-head">
        <span>Coming up</span>
      </div>
      {soon.length === 0 ? (
        <p className="bell-note">Nothing is due in the next 30 days.</p>
      ) : (
        soon.map((item) => (
          <MenuItem
            key={item.id}
            icon={<Icon name={item.urgency === 'overdue' ? 'warn' : 'clock'} size={15} />}
            title={item.title}
            detail={item.due_date ? `Due ${formatDate(item.due_date)}` : 'No date set'}
            onClick={() => {
              onGo();
              void navigate({ to: '/deadlines' });
            }}
          />
        ))
      )}
      <MenuItem
        icon={<Icon name="cal" size={15} />}
        title="See every deadline"
        onClick={() => {
          onGo();
          void navigate({ to: '/deadlines' });
          toast('Showing everything that is coming');
        }}
      />
    </>
  );
}
